import { PaymentMethodTypeEnum } from '../enum/shared/payment-method-type-enum';
import { Deserializable } from '../protocols/deserializable';
import { WithdrawalInstruction } from '../submissions/withdrawal-instruction';
import { RecurringPaymentInstruction } from '../submissions/recurring-payment-instruction';
import { AnnualMinimumDetails } from './annual-minimum-details';
import { PaymentMethodType } from '../types/dto/payment-method-type';
import { LumpSumPaymentInstruction } from './lump-sum-payment-instruction';

export class VariableBenefitAccountDetails implements Deserializable {
  public effectiveDate!: string;
  public totalBalance!: number;
  public yearToDatePaymentAmount!: number;
  public paymentMethodId!: PaymentMethodTypeEnum | null;
  public paymentMethod!: PaymentMethodType | null;
  public paymentBankAccountId!: number | null;
  public paymentBankAccountNickname!: string | null;
  public scheduledPaymentMethodId!: PaymentMethodTypeEnum | null;
  public scheduledPaymentMethod!: PaymentMethodType | null;
  public scheduledPaymentBankAccountId!: number | null;
  public scheduledPaymentBankAccountNickname!: string | null;
  public scheduledPaymentDestinationStartDate!: string | null;
  public nextPaymentDate!: string;
  public nextPaymentAmount!: number;
  public activeMonthlyRecurringPaymentInstruction!: RecurringPaymentInstruction | null;
  public activeAnnualRecurringPaymentInstruction!: RecurringPaymentInstruction | null;
  public scheduledMonthlyRecurringPaymentInstruction!: RecurringPaymentInstruction | null;
  public scheduledAnnualRecurringPaymentInstruction!: RecurringPaymentInstruction | null;
  public forgoRecurringPayments: boolean = false;
  public scheduledLumpSumPaymentInstruction!: LumpSumPaymentInstruction | null;
  public historicalLumpSumPayments!: LumpSumPaymentInstruction[];

  public currentYearAnnualMinimumDetails!: AnnualMinimumDetails;
  public nextYearAnnualMinimumDetails!: AnnualMinimumDetails | null;
  public withdrawalInstructions!: WithdrawalInstruction[];

  public onDeserialize(): void {
    const Deserialize = window?.injector?.Deserialize;

    if (this.paymentMethod) {
      this.paymentMethod = Deserialize.instanceOf(PaymentMethodType, this.paymentMethod);
    }

    if (this.scheduledPaymentMethod) {
      this.scheduledPaymentMethod = Deserialize.instanceOf(PaymentMethodType, this.scheduledPaymentMethod);
    }

    if (this.currentYearAnnualMinimumDetails) {
      this.currentYearAnnualMinimumDetails = Deserialize.instanceOf(
        AnnualMinimumDetails,
        this.currentYearAnnualMinimumDetails
      );
    }

    if (this.nextYearAnnualMinimumDetails) {
      this.nextYearAnnualMinimumDetails = Deserialize.instanceOf(
        AnnualMinimumDetails,
        this.nextYearAnnualMinimumDetails
      );
    }

    if (this.activeMonthlyRecurringPaymentInstruction) {
      this.activeMonthlyRecurringPaymentInstruction = Deserialize.instanceOf(
        RecurringPaymentInstruction,
        this.activeMonthlyRecurringPaymentInstruction
      );
    }

    if (this.activeAnnualRecurringPaymentInstruction) {
      this.activeAnnualRecurringPaymentInstruction = Deserialize.instanceOf(
        RecurringPaymentInstruction,
        this.activeAnnualRecurringPaymentInstruction
      );
    }

    if (this.scheduledMonthlyRecurringPaymentInstruction) {
      this.scheduledMonthlyRecurringPaymentInstruction = Deserialize.instanceOf(
        RecurringPaymentInstruction,
        this.scheduledMonthlyRecurringPaymentInstruction
      );
    }

    if (this.scheduledAnnualRecurringPaymentInstruction) {
      this.scheduledAnnualRecurringPaymentInstruction = Deserialize.instanceOf(
        RecurringPaymentInstruction,
        this.scheduledAnnualRecurringPaymentInstruction
      );
    }

    if (this.scheduledLumpSumPaymentInstruction) {
      this.scheduledLumpSumPaymentInstruction = Deserialize.instanceOf(
        LumpSumPaymentInstruction,
        this.scheduledLumpSumPaymentInstruction
      );
    }

    if (this.historicalLumpSumPayments) {
      this.historicalLumpSumPayments = Deserialize.arrayOf(LumpSumPaymentInstruction, this.historicalLumpSumPayments);
    }

    if (this.withdrawalInstructions) {
      this.withdrawalInstructions = Deserialize.arrayOf(WithdrawalInstruction, this.withdrawalInstructions);
    }
  }

  public scheduledOrCurrentPaymentBankAccountId(): number | null {
    return this.hasScheduledPaymentDestination() ? this.scheduledPaymentBankAccountId : this.paymentBankAccountId;
  }

  public scheduledOrCurrentPaymentIsCheque(): boolean {
    const methodId = this.hasScheduledPaymentDestination() ? this.scheduledPaymentMethodId : this.paymentMethodId;
    return methodId === PaymentMethodTypeEnum.Cheque;
  }

  private hasScheduledPaymentDestination(): boolean {
    return !!this.scheduledPaymentMethodId;
  }

  public paymentBankAccountIds(): number[] {
    return [this.paymentBankAccountId, this.scheduledPaymentBankAccountId].filter(
      (id): id is number => typeof id === 'number'
    );
  }
}
